import { defineStore } from 'pinia';
import type { Fournisseur, ParametresRecherche } from '~/types/typesFournisseur';

export const useFournisseursStore = defineStore('fournisseurs', () => {
  const fournisseurRecommande = ref<Fournisseur | null>();
  const fournisseurFavori = ref<Fournisseur | null>();
  const listeFournisseurs = ref<Fournisseur[] | []>(JSON.parse(localStorage.getItem('listeFournisseurs') ?? '[]'));
  const derniereRecherche = ref<ParametresRecherche | null>(
    JSON.parse(localStorage.getItem('derniereRechercheFournisseurs') ?? 'null'),
  );
  const authentificationStore = useAuthentificationStore();
  const vehiculeStore = useVehiculeStore();
  const loaderStore = useLoaderStore();
  const toasterStore = useToasterStore();

  watch(
    listeFournisseurs,
    (newValue) => {
      if (newValue?.length) localStorage.setItem('listeFournisseurs', JSON.stringify(newValue));
      else localStorage.removeItem('listeFournisseurs');
    },
    {deep: true},
  );
  watch(derniereRecherche, (newValue) => {
    if (newValue) localStorage.setItem('derniereRechercheFournisseurs', JSON.stringify(newValue));
    else localStorage.removeItem('derniereRechercheFournisseurs');
  });

  function $reset() {
    fournisseurRecommande.value = null;
    fournisseurFavori.value = null;
    listeFournisseurs.value = [];
    derniereRecherche.value = null;
    localStorage.removeItem('listeFournisseurs');
    localStorage.removeItem('derniereRechercheFournisseurs');
  }

  const setFournisseurFavori = (data?: Fournisseur) => {
    fournisseurFavori.value = data;
  };

  const setListeFournisseurs = async (data?: Fournisseur[]) => {
    listeFournisseurs.value = data;
  };

  const getDerniereRecherche = () => derniereRecherche.value;

  const leFournisseurRecommande = computed(() => listeFournisseurs.value?.filter((f) => f.recommande)[0] ?? null);
  const getFournisseurFavori = computed(() => fournisseurFavori.value ?? null);
  const laListeFournisseursHorsRecommande = computed(() => listeFournisseurs.value?.filter((f) => !f.recommande) ?? []);

  const fetchFournisseurFavori = async () => {
    const { data, error } = await useMyFetch<Fournisseur>(
      '/copilot/vehicules/' + vehiculeStore.getIdActif() + '/favori',
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + authentificationStore.getToken(),
        },
        params: { numeroDossier: vehiculeStore.getNumeroDossier() },
      },
    );
    if (data.value) {
      setFournisseurFavori({ ...data.value, urlLogo: data.value.logo ? data.value.logo : '/logo_app/cle.png' });
    } else if (error.value) {
      toasterStore.afficherLeToaster('error', 'messageErreur.erreurRecupFournisseurFavori');
    } else {
      setFournisseurFavori(undefined);
    }
  };
  const fetchFournisseurs = async (params: ParametresRecherche) => {
    loaderStore.showLoader();
    // On ne conserve pas le recaptchaToken : à usage unique, inutile à rejouer une recherche en cache
    derniereRecherche.value = { ...params, recaptchaToken: '' };

    try {
      const { data, error } = await useMyFetch<Fournisseur[]>('/copilot/fournisseurs/appliConducteur', {
        method: 'GET',
        headers: {
          'X-Recaptcha-Token': params.recaptchaToken,
        },
        params: { ...params, idVehicule: vehiculeStore.getIdActif() },
      });

      if (data.value) {
        if (data.value.length === 0) {
          toasterStore.afficherLeToaster('error', 'messageErreur.erreurAucunFournisseur');
          useLogging(
            {
              action: 'AUCUN_FOURNISSEUR_TROUVE',
              details: {
                prestation: params.prestations,
                latitudeUtilisateur: params.latitude,
                longitudeUtilisateur: params.longitude,
              },
            },
            'logEventButtonClick',
          );
        } else {
          const fournisseursWithLogos = data.value.map((f: Fournisseur) => {
            return { ...f, urlLogo: f.logo ? f.logo : '/logo_app/cle.png' };
          });
          setListeFournisseurs(fournisseursWithLogos);
          useLogging(
            {
              action: 'RESULTATS_RECHERCHE_FOURNISSEURS',
              details: {
                prestation: params.prestations,
                latitudeUtilisateur: params.latitude,
                longitudeUtilisateur: params.longitude,
                idFournisseurRecommande: leFournisseurRecommande.value?.id,
                idsFournisseursAutorisesParRang: fournisseursWithLogos.reduce(
                  (acc, fournisseur, index) => {
                    acc[index + 1] = fournisseur.id; // Utilise index + 1 comme clé
                    return acc;
                  },
                  {} as Record<number, string>,
                ),
              },
            },
            'logEventButtonClick',
          );
        }
      } else if (error.value) {
        toasterStore.afficherLeToaster('error', 'messageErreur.erreurRecupFournisseurs');
      } else {
        setListeFournisseurs([]);
      }
    } finally {
      loaderStore.hideLoader();
    }
  };

  const noterFournisseur = async (notationDTO: NotationDTO) => {
    loaderStore.showLoader();
    const { error } = await useMyFetch<string>('/copilot/fournisseurs/' + vehiculeStore.getIdActif() + '/note', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + authentificationStore.getToken(),
      },
      body: notationDTO,
    });
    if (!error.value) {
      toasterStore.afficherLeToaster('success', 'messageSucces.fournisseurNote');
      useLogging(
        {
          action: 'NOTER_FOURNISSEUR',
          details: {
            idFournisseurChoisi: notationDTO.idFournisseur,
            note: notationDTO.note,
            commentaire: notationDTO.commentaire,
          },
        },
        'logEventButtonClick',
      );
    } else {
      toasterStore.afficherLeToaster('error', 'messageErreur.erreurNotationFournisseur');
    }
    loaderStore.hideLoader();
  };

  const ajouterFournisseurFavori = async (idFournisseur: string) => {
    loaderStore.showLoader();
    try {
      if (fournisseurFavori.value?.id === idFournisseur) {
        await useMyFetch<string>('/copilot/vehicules/' + vehiculeStore.getIdActif() + '/favori', {
          method: 'DELETE',
          body: {
            idFournisseur: idFournisseur,
            numeroDossier: vehiculeStore.getNumeroDossier(),
            createdAt: new Date(Date.now()),
          },
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authentificationStore.getToken(),
          },
        });
        toasterStore.afficherLeToaster('success', 'messageSucces.fournisseurSupprime');
        useLogging(
          {
            action: 'SUPPRIMER_FOURNISSEUR_FAVORI',
            details: {
              idFournisseurChoisi: idFournisseur,
            },
          },
          'logEventButtonClick',
        );
      } else {
        await useMyFetch<string>('/copilot/vehicules/' + vehiculeStore.getIdActif() + '/favori', {
          method: 'POST',
          body: {
            idFournisseur: idFournisseur,
            numeroDossier: vehiculeStore.getNumeroDossier(),
            createdAt: new Date(Date.now()),
          },
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + authentificationStore.getToken(),
          },
        });
        toasterStore.afficherLeToaster('success', 'messageSucces.fournisseurAjoute');
        useLogging(
          {
            action: 'AJOUTER_FOURNISSEUR_FAVORI',
            details: {
              idFournisseurChoisi: idFournisseur,
            },
          },
          'logEventButtonClick',
        );
      }

    } catch (e) {
      toasterStore.afficherLeToaster('error', 'messageErreur.erreurAjoutFourisseurFavori');
    }

    fetchFournisseurFavori();
    loaderStore.hideLoader();
  };

  return {
    $reset,
    fetchFournisseurs,
    fetchFournisseurFavori,
    setFournisseurFavori,
    getFournisseurFavori,
    getDerniereRecherche,
    leFournisseurRecommande,
    laListeFournisseursHorsRecommande,
    noterFournisseur,
    ajouterFournisseurFavori,
  };
});
